import type { EventContext, EventName, LogisticInfo } from '../types';
import { getBrand } from './brand';
import { getItems } from './items';
import { getPaymentType } from './paymentType';
import { documentToHash, getLocalStorage, setLocalStorage, toNum } from '../core/utils';


export const paramGetters = {
  brand: getBrand,

  items: getItems,

  payment_type: getPaymentType,

  user_info: async (context: EventContext, eventName: EventName) => {
    const PROD_DOMAINS: Record<string, Record<string, string>> = {
      "animale": {
        "store": "https://www.animale.com.br",
        "checkout": "https://secure.animale.com.br",
      },
      "farm": {
        "store": "https://lojafarm.myvtex.com"
      },
      "mariafilo": {
        "store": "https://mariafilo.myvtex.com",
        "checkout": "https://secure.mariafilo.com.br",
      },
      "lojacrisbarros": {
        "store": "https://lojacrisbarros.myvtex.com",
        "checkout": "https://secure.crisbarros.com.br",
      },
      "lojaoffpremium": {
        "store": "https://lojaoffpremium.myvtex.com",
        "checkout": "https://secure.lojaoffpremium.com.br",
      },
    };

    const domain = PROD_DOMAINS[context.account || 'https://www.animale.com.br']?.[context.platform || 'store'];

    if (!domain) return { customer: { email: context.userEmail, id: context?.userId }, address: null };

    try {
      const api = `${domain}/_v/user-info`;
      const response = await fetch(api, {
        method: 'POST',
        body: JSON.stringify({ email: context.userEmail })
      });

      if (response.status !== 200) {
        return { customer: { email: context.userEmail, id: context?.userId }, address: null };
      }

      const res = await response.json();
      const cpfFromStorage = getLocalStorage('user_doc_dt')

      if (cpfFromStorage) {
        return { ...res, customer: { ...res.customer, cpf: cpfFromStorage }}
      } else {
        const hashedDocument = await documentToHash(res.customer.document)
        console.log('[DT] set localstorage')
        setLocalStorage('user_doc_dt', hashedDocument)

        return { ...res, customer: { ...res.customer, cpf: hashedDocument }}
      }
    } catch (_) {
      return { customer: { email: context.userEmail, id: context?.userId }, address: null };
    }
  },

  content_type: (context: EventContext, eventName: EventName) => {
    return `regiao`
      .concat(':' + context.category)
      .concat(context.subcategory ? ':' + context.subcategory : '')
      .concat(':' + context.componentName) || null
  },

  region: (context: EventContext, eventName: EventName) => {
    return context.region || null;
  },

  line_items: (context: EventContext, eventName: EventName) => {
    if (context?.lineItems) return context.lineItems;

    if (context?.item) {
      return [context.item.productId || context.item.productID || context.item.item_id];
    }

    if (context?.list) {
      return context.list.map((item: { productID: string, id: string, productId: string }) => item?.productID || item?.id || item?.productId)
    }

    return (context.orderForm.items || [])
      .map((item: EventContext) => item.productId)
      .join(',');
  },

  currency: (context: EventContext, eventName: EventName) => {
    if (context?.currency) return context.currency

    if (context?.bannerUrl) return 'BRL'

    if (context?.item) {
      return context.item?.offers?.priceCurrency || 'BRL'
    }

    return context.orderForm?.storePreferencesData?.currencyCode || null;
  },

  value: (context: EventContext, eventName: EventName) => {
    if (context?.value) return toNum(context.value)

    return context.item ? toNum(context.item?.price) : context.orderForm?.value != null
      ? context.orderForm.value / 100
      : null;
  },

  total_discount: (context: EventContext, eventName: EventName) => {
    if (context?.totalDiscount) return context.totalDiscount

    const totalsList = context.orderForm?.totalizers || context.orderForm?.totals || []; // totals for ordergroup and orderform have different names
    const items = context.orderForm.items;
    const totalDiscount = items.reduce((acc: number, item: { quantity: number, listPrice: number, sellingPrice: number }) => {
      const unitDiscount = Math.max(0, item.listPrice - item.sellingPrice);
      return acc + (unitDiscount * item.quantity);
    }, 0);

    const discounts = totalsList.find((t: { id: string }) => t.id === 'Discounts');
    return Math.abs((discounts?.value || 0) + totalDiscount) / 100 || null;
  },

  subtotal: (context: EventContext, eventName: EventName) => {
    const totalsList = context.orderForm?.totalizers || context.orderForm?.totals || []; // totals for ordergroup and orderform have different names

    const subtotal =
      totalsList
        .filter((t: EventContext) => t.id === 'Items')
        .reduce((acc: number, t: EventContext) => acc + (t.value || 0), 0) / 100 || 0;

    return subtotal
  },

  coupon_message: (context: EventContext, eventName: EventName) => {
    if (context.couponMessage) return context.couponMessage;

    const messages = context.orderForm?.messages || [];

    if (messages.length === 0) return null;

    const couponMessages = messages.filter((msg: EventContext) =>
      msg.text.toLowerCase().includes('cupom')
    );

    if (couponMessages.length === 0) return null;

    return couponMessages.map((msg: EventContext) => msg.text).join(' | ');
  },

  seller_cod_name: (context: EventContext, eventName: EventName) => {
    return context.appliedSellerCodName || context?.orderForm.marketingData.utmiCampaign || null;
  },

  coupon: (context: EventContext, eventName: EventName) => {
    return context.appliedCoupon || context?.orderForm?.marketingData?.coupon || null;
  },

  pre_filled: (context: EventContext, eventName: EventName) => {
    return !!context.preFilled;
  },

  search_term: (context: EventContext, eventName: EventName) => {
    return context.searchTerm || null;
  },

  search_found: (context: EventContext, eventName: EventName) => {
    return !!context.searchFound;
  },

  search_quantity: (context: EventContext, eventName: EventName) => {
    return context.searchQuantity || 0;
  },

  promotion_name: (context: EventContext, eventName: EventName) => {
    return context.promotionName || null;
  },

  creative_name: (context: EventContext, eventName: EventName) => {
    return context.creativeName || null;
  },

  creative_slot: (context: EventContext, eventName: EventName) => {
    return context.creativeSlot || null;
  },

  zipcode: (context: EventContext, eventName: EventName) => {
    return context.zipCode || null;
  },

  size: (context: EventContext, eventName: EventName) => {
    return context.size || null;
  },

  item_ref: (context: EventContext, eventName: EventName) => {
    return context.itemRef || null;
  },

  flag_pickup: (context: EventContext, eventName: EventName) => {
    const orderForm = context.orderForm;
    if (!orderForm) return null;

    const logisticsInfo = orderForm.shippingData?.logisticsInfo || [];
    const hasPickup = logisticsInfo.some((info: LogisticInfo) =>
      info.slas?.some((sla) => sla.deliveryChannel === 'pickup-in-point')
    );

    return hasPickup
  },

  shippings: (context: EventContext, eventName: EventName) => {
    const orderForm = context.orderForm;
    if (!orderForm) return null;

    const groupShipping: Record<string, any> = {};

    for (const info of orderForm.shippingData.logisticsInfo) {
      const item = orderForm.items[info.itemIndex]
      if (!item) continue

      for (const sla of info.slas) {
        const key = sla.name

        if (!groupShipping[key]) {
          groupShipping[key] = {
            shipping_tier: sla.name,
            delivery_time: parseInt(sla.shippingEstimate),
            shipping: parseFloat((sla.price / 100).toFixed(1)),
            line_items: []
          }
        }

        groupShipping[key].line_items.push(item.id)
      }
    }

    const shippings = Object.values(groupShipping).map((s) => ({
      shipping: s.shipping,
      shipping_tier: s.shipping_tier,
      delivery_time: s.delivery_time,
      line_items: s.line_items.join(',')
    }))

    return shippings || null
  },

  shipping: (context: EventContext, eventName: EventName) => {
    const logistics = context?.orderForm?.shippingData?.logisticsInfo ?? []
    return logistics.reduce((total: number, item: any) => {
      const selected = item.slas?.find((sla: any) => sla.id === item.selectedSla)
      return total + (selected?.price ?? 0)
    }, 0)
  },

  shipping_tier: (context: EventContext, eventName: EventName) => {
    const logistics = context?.orderForm?.shippingData?.logisticsInfo ?? []
    const selectedSlas = logistics
      .map((item: {
        selectedSla?: string,
        slas?: Array<{
          id: string,
          deliveryChannel: string,
          name?: string,
          pickupStoreInfo?: { friendlyName?: string }
        }>
      }) => {
        if (!item.selectedSla) return null;
        return item.slas?.find((sla) => sla.id === item.selectedSla) || null;
      })
      .filter(Boolean) as Array<{
        deliveryChannel: string,
        name?: string,
        pickupStoreInfo?: { friendlyName?: string }
      }>;

    if (selectedSlas.length === 0) return null;

    const uniqueTiers = Array.from(
      new Map(
        selectedSlas.map((sla) => {
          const key = sla.deliveryChannel === 'pickup-in-point'
            ? `pickup:${sla.pickupStoreInfo?.friendlyName || sla.name || ''}`
            : `delivery:${sla.name || ''}`;
          return [key, sla];
        })
      ).values()
    );

    const sortedTiers = uniqueTiers.sort((a, b) => {
      const aOrder = a.deliveryChannel === 'pickup-in-point' ? 0 : 1;
      const bOrder = b.deliveryChannel === 'pickup-in-point' ? 0 : 1;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const aLabel = a.deliveryChannel === 'pickup-in-point'
        ? (a.pickupStoreInfo?.friendlyName || a.name || '')
        : (a.name || '');
      const bLabel = b.deliveryChannel === 'pickup-in-point'
        ? (b.pickupStoreInfo?.friendlyName || b.name || '')
        : (b.name || '');

      return aLabel.localeCompare(bLabel, 'pt-BR');
    });

    const shippingTier = sortedTiers
      .map((sla) => (
        sla.deliveryChannel === 'pickup-in-point'
          ? `retirada em loja${sla.pickupStoreInfo?.friendlyName ? `: ${sla.pickupStoreInfo.friendlyName}` : ''}`
          : sla.deliveryChannel === 'delivery'
            ? `receba em casa${sla.name ? `: ${sla.name}` : ''}`
            : null
      ))
      .filter(Boolean)
      .join(', ');

    return shippingTier || null;
  },

  transaction_id: (context: EventContext, eventName: EventName) => {
    return context.orderForm?.orderGroup?.toString() || null;
  },

  available_grid: (context: EventContext, eventName: EventName) => {
    return context.availableGrid || null;
  },
}